import { api } from "@/redux/api";
import {
  ApiResponse,
  Notification,
  NotificationStats,
  NotificationPreferences,
  GetNotificationsRequest,
  GetNotificationsResponse,
  CreateNotificationRequest,
  UpdateNotificationPreferencesRequest,
  UpdatePushTokenRequest,
} from "@/types/api";

const notificationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // GET /api/v1/notifications - Get user notifications with pagination and filtering
    getNotifications: builder.query<
      ApiResponse<GetNotificationsResponse>,
      GetNotificationsRequest
    >({
      query: (params) => ({
        url: "/notifications",
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result?.data?.notifications
          ? [
              ...result.data.notifications.map(({ _id }) => ({
                type: "Notifications" as const,
                id: _id,
              })),
              { type: "Notifications", id: "LIST" },
            ]
          : [{ type: "Notifications", id: "LIST" }],
    }),

    // GET /api/v1/notifications/:id - Get specific notification
    getNotificationById: builder.query<
      ApiResponse<{ notification: Notification }>,
      string
    >({
      query: (id) => ({
        url: `/notifications/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Notifications", id }],
    }),

    // PUT /api/v1/notifications/:id/read - Mark notification as read
    markNotificationAsRead: builder.mutation<
      ApiResponse<{ notification: Notification }>,
      string
    >({
      query: (id) => ({
        url: `/notifications/${id}/read`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Notifications", id },
        { type: "Notifications", id: "LIST" },
        { type: "Notifications", id: "STATS" },
      ],
      // Optimistic update
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          notificationApi.util.updateQueryData(
            "getNotifications",
            {},
            (draft) => {
              const notification = draft.data?.notifications.find(
                (n) => n._id === id
              );
              if (notification) {
                notification.read = true;
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // POST /api/v1/notifications/mark-all-read - Mark all notifications as read
    markAllNotificationsAsRead: builder.mutation<
      ApiResponse<{ modifiedCount: number }>,
      void
    >({
      query: () => ({
        url: "/notifications/mark-all-read",
        method: "POST",
      }),
      invalidatesTags: [
        { type: "Notifications", id: "LIST" },
        { type: "Notifications", id: "STATS" },
      ],
      // Optimistic update
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          notificationApi.util.updateQueryData(
            "getNotifications",
            {},
            (draft) => {
              if (draft.data?.notifications) {
                draft.data.notifications.forEach((notification) => {
                  notification.read = true;
                });
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // DELETE /api/v1/notifications/:id - Delete notification
    deleteNotification: builder.mutation<
      ApiResponse<{ message: string }>,
      string
    >({
      query: (id) => ({
        url: `/notifications/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Notifications", id },
        { type: "Notifications", id: "LIST" },
        { type: "Notifications", id: "STATS" },
      ],
      // Optimistic update
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          notificationApi.util.updateQueryData(
            "getNotifications",
            {},
            (draft) => {
              if (draft.data?.notifications) {
                draft.data.notifications = draft.data.notifications.filter(
                  (n) => n._id !== id
                );
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // POST /api/v1/notifications - Create notification (admin only)
    createNotification: builder.mutation<
      ApiResponse<{ notification: Notification }>,
      CreateNotificationRequest
    >({
      query: (body) => ({
        url: "/notifications",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Notifications", id: "LIST" },
        { type: "Notifications", id: "STATS" },
      ],
    }),

    // GET /api/v1/notifications/stats - Get notification statistics
    getNotificationStats: builder.query<ApiResponse<NotificationStats>, void>({
      query: () => ({
        url: "/notifications/stats",
        method: "GET",
      }),
      providesTags: [{ type: "Notifications", id: "STATS" }],
    }),

    // GET /api/v1/users/:id/notification-preferences - Get user notification preferences
    getNotificationPreferences: builder.query<
      ApiResponse<{ preferences: NotificationPreferences }>,
      string
    >({
      query: (userId) => ({
        url: `/users/${userId}/notification-preferences`,
        method: "GET",
      }),
      providesTags: (result, error, userId) => [
        { type: "User", id: userId },
        { type: "Notifications", id: "PREFERENCES" },
      ],
    }),

    // PUT /api/v1/users/:id/notification-preferences - Update user notification preferences
    updateNotificationPreferences: builder.mutation<
      ApiResponse<{ preferences: NotificationPreferences }>,
      { userId: string } & UpdateNotificationPreferencesRequest
    >({
      query: ({ userId, ...body }) => ({
        url: `/users/${userId}/notification-preferences`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "User", id: userId },
        { type: "Notifications", id: "PREFERENCES" },
      ],
    }),

    // PUT /api/v1/users/:id/push-token - Update user push token
    updatePushToken: builder.mutation<
      ApiResponse<{ pushToken: string }>,
      { userId: string } & UpdatePushTokenRequest
    >({
      query: ({ userId, ...body }) => ({
        url: `/users/${userId}/push-token`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "User", id: userId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetNotificationsQuery,
  useGetNotificationByIdQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useDeleteNotificationMutation,
  useCreateNotificationMutation,
  useGetNotificationStatsQuery,
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  useUpdatePushTokenMutation,
} = notificationApi;

export { notificationApi };
